import api from '../axiosConfig';
import { HOUSEKEEPING } from '../config/apiEndpoints';

export const HousekeepingService = {
    /**
     * Create a new housekeeping task
     * @param {Object} payload The task data
     * @param {string} tenantId The active tenant ID to append to headers
     * @returns {Promise<Object>}
     */
    createTask: async (payload, tenantId) => {
        try {
            const response = await api.post(HOUSEKEEPING.TASKS, payload, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || new Error('Failed to create housekeeping task');
        }
    },

    /**
     * Update an existing housekeeping task
     * @param {string} id The task ID
     * @param {Object} payload The fields to update
     * @param {string} tenantId The active tenant ID
     * @returns {Promise<Object>}
     */
    updateTask: async (id, payload, tenantId) => {
        try {
            const response = await api.patch(HOUSEKEEPING.TASK_BY_ID(id), payload, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || new Error('Failed to update housekeeping task');
        }
    },

    /**
     * Get all housekeeping tasks
     * @param {string} tenantId 
     * @returns {Promise<Array>}
     */
    getAllTasks: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(HOUSEKEEPING.TASKS, {
                headers: { 'x-tenant-id': tenantId },
                params: axiosParams
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch housekeeping tasks:", error);
            return []; // Fallback empty
        }
    },

    /**
     * Get a specific housekeeping task by ID
     * @param {string} id The task ID
     * @param {string} tenantId The active tenant ID
     * @returns {Promise<Object>}
     */
    getTaskById: async (id, tenantId) => {
        try {
            const response = await api.get(HOUSEKEEPING.TASK_BY_ID(id), {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || new Error('Failed to fetch housekeeping task details');
        }
    },

    /**
     * Get Housekeeping Dashboard KPIs
     * @param {string} tenantId The active tenant ID
     * @returns {Promise<Object>}
     */
    getDashboardStats: async (tenantId) => {
        try {
            const response = await api.get(HOUSEKEEPING.DASHBOARD, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch housekeeping dashboard stats:", error);
            throw error.response?.data || new Error('Failed to fetch dashboard stats');
        }
    },

    /**
     * Create a new housekeeping task type dictionary entry
     * @param {Object} payload 
     * @param {string} tenantId 
     * @returns {Promise<Object>}
     */
    createTaskType: async (payload, tenantId) => {
        try {
            const response = await api.post(HOUSEKEEPING.TASK_TYPES, payload, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || new Error('Failed to create task type');
        }
    },

    /**
     * Fetch all housekeeping task types
     * @param {string} tenantId 
     * @returns {Promise<Array>}
     */
    getTaskTypes: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(HOUSEKEEPING.TASK_TYPES, {
                headers: { 'x-tenant-id': tenantId },
                params: axiosParams
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch task types:", error);
            return []; // Fallback
        }
    },

    /**
     * Log a new guest item found
     * @param {Object} payload 
     * @param {string} tenantId 
     * @returns {Promise<Object>}
     */
    logLostFoundItem: async (payload, tenantId) => {
        try {
            const response = await api.post(HOUSEKEEPING.LOST_FOUND, payload, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || new Error('Failed to log lost and found item');
        }
    },

    /**
     * Fetch all lost and found items
     * @param {string} tenantId 
     * @returns {Promise<Array>}
     */
    getLostFoundItems: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(HOUSEKEEPING.LOST_FOUND, {
                headers: { 'x-tenant-id': tenantId },
                params: axiosParams
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch lost and found items:", error);
            return []; // Fallback
        }
    },

    /**
     * Claim a guest item
     * @param {string} id The lost-and-found item ID
     * @param {Object} payload The claim data
     * @param {string} tenantId The active tenant ID
     * @returns {Promise<Object>}
     */
    claimLostFoundItem: async (id, payload, tenantId) => {
        try {
            const response = await api.post(HOUSEKEEPING.CLAIM_ITEM(id), payload, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || new Error('Failed to claim item');
        }
    },

    /**
     * Discard a guest item from lost and found registry
     * @param {string} id The lost-and-found item ID
     * @param {string} tenantId The active tenant ID
     * @returns {Promise<Object>}
     */
    discardLostFoundItem: async (id, tenantId) => {
        try {
            const response = await api.post(HOUSEKEEPING.DISCARD_ITEM(id), {}, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || new Error('Failed to discard item');
        }
    },

    /**
     * Get Housekeeper Performance Report
     * @param {string} tenantId The active tenant ID
     * @returns {Promise<Array>}
     */
    getPerformanceReport: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(HOUSEKEEPING.PERFORMANCE_REPORT, {
                headers: { 'x-tenant-id': tenantId },
                params: axiosParams
            });
            return response.data;
        } catch (error) {
            console.error("Failed to fetch housekeeper performance report:", error);
            throw error.response?.data || new Error('Failed to fetch performance report');
        }
    }
};
